// Renders an answer: a small, safe subset of Markdown (paragraphs, "- " and "1." lists, **bold**)
// built as React elements (no HTML injection), with citation markers as numbered links to the
// cited page.

import type { ReactNode } from "react";
import type { Notice, Source } from "./client.js";

const MARK = /(\d+)/;
const INLINE = /(\*\*[^*\n]+\*\*|\d+)/g;

function inline(text: string, sources: Map<number, Source>, key: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const k = `${key}-${i}`;
    const m = MARK.exec(part);
    if (m) {
      const n = Number(m[1]);
      const s = sources.get(n);
      return (
        <sup key={k} className="cite-mark">
          {s ? (
            <a href={s.url} target="_blank" rel="noreferrer" title={`${s.title}, ${s.label}`} aria-label={`Source ${n}: ${s.title}, ${s.label}`}>
              {n}
            </a>
          ) : (
            n
          )}
        </sup>
      );
    }
    if (part.startsWith("**") && part.endsWith("**") && part.length > 4) return <strong key={k}>{part.slice(2, -2)}</strong>;
    return part;
  });
}

const BULLET = /^\s*[-*•]\s+/;
const NUMBER = /^\s*\d+[.)]\s+/;

export function AnswerText({ text, sources }: { text: string; sources: Source[] }) {
  const byN = new Map(sources.map((s) => [s.n, s]));
  const blocks = text.split(/\n{2,}/).filter((b) => b.trim());
  return (
    <div className="answer-text">
      {blocks.map((block, bi) => {
        const lines = block.split("\n").filter((l) => l.trim());
        const key = `b${bi}`;
        if (lines.length && lines.every((l) => BULLET.test(l))) {
          return (
            <ul key={key}>
              {lines.map((l, i) => (
                <li key={i}>{inline(l.replace(BULLET, ""), byN, `${key}-${i}`)}</li>
              ))}
            </ul>
          );
        }
        if (lines.length && lines.every((l) => NUMBER.test(l))) {
          return (
            <ol key={key}>
              {lines.map((l, i) => (
                <li key={i}>{inline(l.replace(NUMBER, ""), byN, `${key}-${i}`)}</li>
              ))}
            </ol>
          );
        }
        // A paragraph that leads into a list ("Two options:\n- a\n- b").
        const firstBullet = lines.findIndex((l) => BULLET.test(l));
        if (firstBullet > 0 && lines.slice(firstBullet).every((l) => BULLET.test(l))) {
          return (
            <div key={key}>
              <p>{inline(lines.slice(0, firstBullet).join(" "), byN, `${key}-p`)}</p>
              <ul>
                {lines.slice(firstBullet).map((l, i) => (
                  <li key={i}>{inline(l.replace(BULLET, ""), byN, `${key}-${i}`)}</li>
                ))}
              </ul>
            </div>
          );
        }
        return <p key={key}>{inline(lines.join("\n"), byN, key)}</p>;
      })}
    </div>
  );
}

export function SourceList({ sources }: { sources: Source[] }) {
  if (!sources.length) return null;
  return (
    <ol className="sources" aria-label="Sources">
      {sources.map((s) => (
        <li key={s.n} value={s.n}>
          <a href={s.url} target="_blank" rel="noreferrer">
            {s.title}
          </a>
          <span className="muted"> · {s.label}</span>
        </li>
      ))}
    </ol>
  );
}

// The fixed notices under an answer (core notices.ts): only once the answer has finished
// streaming, below its sources, in a quiet callout (not a warning). Talk to HR opens the form for
// this answer; the hotline button shows when its URL is configured.
export function AnswerNotices({
  notices,
  pending,
  hotlineUrl,
  onTalk,
}: {
  notices: readonly Notice[] | undefined;
  pending: boolean;
  hotlineUrl: string;
  onTalk: () => void;
}) {
  if (pending || !notices?.length) return null;
  return (
    <div className="answer-notices">
      {notices.map((n) => {
        const hotline = n.actions.includes("hotline") && hotlineUrl;
        return (
          <div key={n.id} className="answer-notice" data-notice={n.id}>
            <p>{n.text}</p>
            {n.actions.includes("talk_to_hr") || hotline ? (
              <div className="actions start">
                {n.actions.includes("talk_to_hr") ? (
                  <button type="button" onClick={onTalk}>
                    Talk to HR
                  </button>
                ) : null}
                {hotline ? (
                  <a href={hotlineUrl} target="_blank" rel="noreferrer">
                    Anonymous reporting hotline
                  </a>
                ) : null}
              </div>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
